<div class="page-container" *ngIf="fee">
  <!-- Page Header -->
  <div class="page-header">
    <div class="fee-name-section">
      <h1 class="fee-name">{{ fee.name }}</h1>
    </div>
    <div class="metadata-badges">
      <div class="metadata-badge">
        <mat-icon class="badge-icon">label_outline</mat-icon>
        <div class="badge-text">
          <span class="badge-label">Type</span>
          <span class="badge-value">{{ fee.type }}</span>
        </div>
      </div>
      <div class="metadata-divider"></div>
      <div class="metadata-badge">
        <mat-icon class="badge-icon">account_tree</mat-icon>
        <div class="badge-text">
          <span class="badge-label">Structure</span>
          <span class="badge-value">{{ fee.structure }}</span>
        </div>
      </div>
      <div class="metadata-divider"></div>
      <div class="metadata-badge">
        <mat-icon class="badge-icon">percent</mat-icon>
        <div class="badge-text">
          <span class="badge-label">Rate type</span>
          <span class="badge-value">{{ fee.rateType }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Tabs -->
  <mat-tab-group animationDuration="0ms" class="fee-tabs">
    <!-- FEE HISTORY TAB -->
    <mat-tab label="FEE HISTORY">
      <div class="tab-content">
        @if (isRangeStructure) {
          <!-- Grouped history cards for Range/tiered fees -->
          @for (card of fee.historyCards; track $index) {
            <div class="history-card">
              <div class="history-card-header">
                <mat-icon class="history-card-icon">inventory_2</mat-icon>
                <div class="header-field">
                  <span class="label">Modified</span>
                  <span class="value">{{ card.modifiedDate }}</span>
                </div>
                <div class="header-field">
                  <span class="label">Effective from</span>
                  <span class="value">{{ card.effectiveFrom }}</span>
                </div>
                <div class="header-field">
                  <span class="label">Maximum</span>
                  <span class="value">{{ card.maximum }}</span>
                </div>
                @if (card.changedBy) {
                  <div class="header-field">
                    <span class="label">Changed by</span>
                    <span class="value">{{ card.changedBy }}</span>
                  </div>
                }
              </div>
              <table class="tier-history-table" aria-label="Fee tier history">
                <thead>
                  <tr>
                    <th>Fee</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  @for (tier of card.tiers; track $index; let i = $index; let last = $last) {
                    <tr [class.highlighted]="last && card.tiers.length > 5">
                      <td>{{ tier.fee }}</td>
                      <td>{{ tier.from }}</td>
                      <td>{{ tier.to }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
          @if (!fee.historyCards || fee.historyCards.length === 0) {
            <p class="no-data-text">No history found for this fee.</p>
          }
        } @else {
          <!-- Flat history table for non-Range fees -->
          <div class="mat-elevation-z1 table-card">
            <table mat-table [dataSource]="historyDataSource" aria-label="Fee history">
              <ng-container matColumnDef="activeDateRange">
                <th mat-header-cell *matHeaderCellDef>Active date range</th>
                <td mat-cell *matCellDef="let row" class="highlight-cell">{{ row.activeDateRange }}</td>
              </ng-container>

              <ng-container matColumnDef="rate">
                <th mat-header-cell *matHeaderCellDef>Rate</th>
                <td mat-cell *matCellDef="let row" class="highlight-cell">{{ row.rate }}</td>
              </ng-container>

              <ng-container matColumnDef="minimum">
                <th mat-header-cell *matHeaderCellDef>Minimum</th>
                <td mat-cell *matCellDef="let row" class="highlight-cell">{{ row.minimum }}</td>
              </ng-container>

              <ng-container matColumnDef="maximum">
                <th mat-header-cell *matHeaderCellDef>Maximum</th>
                <td mat-cell *matCellDef="let row" class="highlight-cell">{{ row.maximum }}</td>
              </ng-container>

              <ng-container matColumnDef="source">
                <th mat-header-cell *matHeaderCellDef>Source</th>
                <td mat-cell *matCellDef="let row">{{ row.source }}</td>
              </ng-container>

              <ng-container matColumnDef="changedBy">
                <th mat-header-cell *matHeaderCellDef>Changed by</th>
                <td mat-cell *matCellDef="let row">{{ row.changedBy }}</td>
              </ng-container>

              <ng-container matColumnDef="changeDate">
                <th mat-header-cell *matHeaderCellDef>Change date</th>
                <td mat-cell *matCellDef="let row">{{ row.changeDate }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="historyColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: historyColumns;"
                class="history-row"
                [ngClass]="{ 'recent-entry': isFirstHistoryEntry(row) }"
              ></tr>
              <tr class="mat-row" *matNoDataRow>
                <td class="mat-cell no-data-cell" [attr.colspan]="historyColumns.length">
                  No history found for this fee.
                </td>
              </tr>
            </table>
          </div>
        }
      </div>
    </mat-tab>

    <!-- PARAMETERS TAB -->
    <mat-tab label="PARAMETERS">
      <div class="tab-content">
        @if (isRangeStructure) {
          <!-- Range/tiered parameters layout -->
          <form [formGroup]="parametersForm" class="range-parameters">
            <!-- Left column: Source and Description -->
            <div class="range-left">
              <div class="form-section">
                <div class="form-label">Source</div>
                <mat-radio-group formControlName="source" class="source-radio-group" aria-label="Source">
                  <mat-radio-button value="All">All</mat-radio-button>
                  <mat-radio-button value="Mail-in">Mail-in</mat-radio-button>
                  <mat-radio-button value="Online">Online</mat-radio-button>
                </mat-radio-group>
              </div>

              <div class="form-section">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Description</mat-label>
                  <textarea
                    matInput
                    formControlName="description"
                    rows="5"
                    aria-label="Description"
                  ></textarea>
                </mat-form-field>
              </div>
            </div>

            <!-- Right column: Tiers table with add/remove, Maximum, Effective from -->
            <div class="range-right">
              <div class="range-right-header">
                <button
                  mat-mini-fab
                  color="primary"
                  class="add-tier-btn"
                  type="button"
                  (click)="addTier()"
                  aria-label="Add tier"
                >
                  <mat-icon>add</mat-icon>
                </button>
              </div>

              <table class="tiers-edit-table" aria-label="Fee tiers">
                <thead>
                  <tr>
                    <th>Fee</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (tier of tiers; track $index; let i = $index) {
                    @if (editingTierIndex === i) {
                      <tr class="editing-row">
                        <td><input class="tier-input" [(ngModel)]="editingTier.fee" placeholder="$0.00 or 5%" [ngModelOptions]="{standalone: true}"></td>
                        <td><input class="tier-input" [(ngModel)]="editingTier.from" placeholder="$0.00" [ngModelOptions]="{standalone: true}"></td>
                        <td><input class="tier-input" [(ngModel)]="editingTier.to" placeholder="$0.00" [ngModelOptions]="{standalone: true}"></td>
                        <td class="action-cell">
                          <button mat-icon-button type="button" (click)="saveTier()" aria-label="Save tier" color="primary">
                            <mat-icon>check</mat-icon>
                          </button>
                          <button mat-icon-button type="button" (click)="cancelEditTier()" aria-label="Cancel edit">
                            <mat-icon>close</mat-icon>
                          </button>
                        </td>
                      </tr>
                    } @else {
                      <tr>
                        <td>{{ tier.fee }}</td>
                        <td>{{ tier.from }}</td>
                        <td>{{ tier.to }}</td>
                        <td class="action-cell">
                          <button mat-icon-button type="button" (click)="editTier(i)" aria-label="Edit tier">
                            <mat-icon>edit</mat-icon>
                          </button>
                          <button mat-icon-button color="warn" type="button" (click)="removeTier(i)" aria-label="Remove tier">
                            <mat-icon>delete</mat-icon>
                          </button>
                        </td>
                      </tr>
                    }
                  }
                  @if (editingTierIndex === -1) {
                    <tr class="editing-row">
                      <td><input class="tier-input" [(ngModel)]="editingTier.fee" placeholder="$0.00 or 5%" [ngModelOptions]="{standalone: true}"></td>
                      <td><input class="tier-input" [(ngModel)]="editingTier.from" placeholder="$0.00" [ngModelOptions]="{standalone: true}"></td>
                      <td><input class="tier-input" [(ngModel)]="editingTier.to" placeholder="$0.00" [ngModelOptions]="{standalone: true}"></td>
                      <td class="action-cell">
                        <button mat-icon-button type="button" (click)="saveTier()" aria-label="Save new tier" color="primary">
                          <mat-icon>check</mat-icon>
                        </button>
                        <button mat-icon-button type="button" (click)="cancelEditTier()" aria-label="Cancel">
                          <mat-icon>close</mat-icon>
                        </button>
                      </td>
                    </tr>
                  }
                  @if (tiers.length === 0 && editingTierIndex !== -1) {
                    <tr>
                      <td colspan="4" class="no-tiers-cell">No tiers defined. Click + to add one.</td>
                    </tr>
                  }
                </tbody>
              </table>

              <div class="range-bottom-fields">
                <mat-form-field appearance="outline">
                  <mat-label>Maximum</mat-label>
                  <span matPrefix>$&nbsp;</span>
                  <input
                    matInput
                    type="number"
                    formControlName="maximum"
                    aria-label="Maximum value"
                  />
                </mat-form-field>

                <mat-form-field
                  appearance="outline"
                  [ngClass]="{ 'date-field-active': showWarningBanner }"
                >
                  <mat-label>Effective from</mat-label>
                  <input
                    matInput
                    [matDatepicker]="rangePicker"
                    formControlName="effectiveFrom"
                    aria-label="Effective from date"
                  />
                  <mat-datepicker-toggle matSuffix [for]="rangePicker"></mat-datepicker-toggle>
                  <mat-datepicker #rangePicker></mat-datepicker>
                </mat-form-field>
              </div>

              <!-- Warning banner for Range -->
              <div class="warning-banner" *ngIf="showWarningBanner" role="alert">
                <mat-icon class="warning-icon">warning</mat-icon>
                <span class="warning-text">
                  Changes have been made on this fee, these will take effect on
                  <strong>{{ warningEffectiveDate }}</strong>
                </span>
                <button
                  mat-stroked-button
                  class="cancel-warning-btn"
                  type="button"
                  (click)="cancelDateChange()"
                  aria-label="Cancel date change"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </form>
        } @else {
          <!-- Flat rate parameters layout -->
          <form [formGroup]="parametersForm" class="parameters-form">
            <!-- Rate / Minimum / Maximum row -->
            <div class="rate-row">
              <div class="field-group">
                <label class="static-label">Rate (%) <span class="required">*</span></label>
                <mat-form-field appearance="outline" class="rate-field" subscriptSizing="dynamic">
                  <input matInput type="number" formControlName="rateValue" placeholder="0.00" aria-label="Rate value">
                </mat-form-field>
              </div>

              <div class="field-group">
                <label class="static-label">Minimum</label>
                <mat-form-field appearance="outline" class="rate-field" subscriptSizing="dynamic">
                  <span matPrefix>$&nbsp;</span>
                  <input matInput type="number" formControlName="minimum" placeholder="0.00" aria-label="Minimum value">
                </mat-form-field>
              </div>

              <div class="field-group">
                <label class="static-label">Maximum</label>
                <mat-form-field appearance="outline" class="rate-field" subscriptSizing="dynamic">
                  <span matPrefix>$&nbsp;</span>
                  <input matInput type="number" formControlName="maximum" placeholder="0.00" aria-label="Maximum value">
                </mat-form-field>
              </div>
            </div>

            <!-- Source radio group -->
            <div class="form-section">
              <label class="static-label">Source</label>
              <mat-radio-group formControlName="source" class="source-radio-group" aria-label="Source">
                <mat-radio-button value="All">All</mat-radio-button>
                <mat-radio-button value="Mail-in">Mail-in</mat-radio-button>
                <mat-radio-button value="Online">Online</mat-radio-button>
              </mat-radio-group>
            </div>

            <!-- Description -->
            <div class="form-section">
              <label class="static-label">Description</label>
              <mat-form-field appearance="outline" class="full-width" subscriptSizing="dynamic">
                <textarea matInput formControlName="description" rows="4" placeholder="Enter fee description" aria-label="Description"></textarea>
              </mat-form-field>
            </div>

            <!-- Effective from date picker -->
            <div class="form-section">
              <label class="static-label">Effective from</label>
              <mat-form-field
                appearance="outline"
                class="date-field"
                subscriptSizing="dynamic"
                [ngClass]="{ 'date-field-active': showWarningBanner }"
              >
                <input matInput [matDatepicker]="effectivePicker" formControlName="effectiveFrom" placeholder="Select date" aria-label="Effective from date">
                <mat-datepicker-toggle matSuffix [for]="effectivePicker"></mat-datepicker-toggle>
                <mat-datepicker #effectivePicker></mat-datepicker>
              </mat-form-field>

              <!-- Warning banner -->
              <div class="warning-banner" *ngIf="showWarningBanner" role="alert">
                <mat-icon class="warning-icon">warning</mat-icon>
                <span class="warning-text">
                  Changes have been made on this fee, these will take effect on
                  <strong>{{ warningEffectiveDate }}</strong>
                </span>
                <button
                  mat-stroked-button
                  class="cancel-warning-btn"
                  type="button"
                  (click)="cancelDateChange()"
                  aria-label="Cancel date change"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </form>
        }
      </div>
    </mat-tab>
  </mat-tab-group>

  <!-- Footer Actions -->
  <div class="footer-actions">
    <button mat-stroked-button class="go-back-btn" type="button" (click)="goBack()" aria-label="Go back">
      GO BACK
    </button>
    <button
      mat-flat-button
      color="primary"
      class="save-btn"
      type="button"
      (click)="onSave()"
      [disabled]="parametersForm.invalid || isLoading"
      aria-label="Save changes"
    >
      @if (isLoading) {
        <mat-spinner diameter="18"></mat-spinner>
      } @else {
        <mat-icon>save</mat-icon>
      }
      SAVE
    </button>
  </div>
</div>
